import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '../../components/layout/AppLayout';
import api from '../../lib/api';
import { CheckCircle2, AlertTriangle, Loader2, ArrowRight } from 'lucide-react';

const POLL_MS = 2000;
const MAX_ATTEMPTS = 30; // ~60 seconds total

const CustomerPaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'checking', amount: null, currency: 'eur', payment_status: null, job_id: null });
  const attemptsRef = useRef(0);

  const sessionId = params.get('session_id');
  const initialJobId = params.get('job_id');

  useEffect(() => {
    if (!sessionId) {
      // Defer to avoid set-state-in-effect lint warning
      const t = setTimeout(() => setState({ status: 'error', payment_status: 'missing_session' }), 0);
      return () => clearTimeout(t);
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await api.get(`/payments/checkout/status/${sessionId}`);
        const ps = res.data.payment_status;
        const st = res.data.status;
        setState({
          status: ps === 'paid' ? 'paid' : (st === 'expired' ? 'expired' : 'checking'),
          amount: res.data.amount_total != null ? res.data.amount_total / 100 : null,
          currency: res.data.currency || 'eur',
          payment_status: ps,
          job_id: res.data.job_id || initialJobId,
        });
        if (ps === 'paid' || st === 'expired') return; // stop
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState((s) => ({ ...s, status: 'timeout' }));
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (err) {
        setState({ status: 'error', payment_status: err?.response?.data?.detail || 'unknown', job_id: initialJobId });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, initialJobId]);

  const ok = state.status === 'paid';
  const expired = state.status === 'expired';
  const checking = state.status === 'checking';
  const error = state.status === 'error';
  const timeout = state.status === 'timeout';

  return (
    <AppLayout>
      <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-2xl card-subtle p-10 mt-8">
        {checking && (
          <div className="text-center" data-testid="payment-status-checking">
            <Loader2 className="w-12 h-12 text-[#001d4c] animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-[#001d4c]">Zahlung wird geprüft …</h1>
            <p className="text-sm text-slate-600 mt-2">Wir bestätigen den Zahlungseingang. Bitte einen Moment Geduld.</p>
          </div>
        )}
        {ok && (
          <div className="text-center" data-testid="payment-status-success">
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-[#001d4c]">Zahlung erfolgreich</h1>
            <p className="text-sm text-slate-600 mt-2">
              Vielen Dank! Ihre Buchung ist bestätigt.
              {state.amount != null && (
                <span className="block mt-1">
                  Betrag: <span className="font-mono font-semibold">{state.amount.toFixed(2)} {(state.currency || 'EUR').toUpperCase()}</span>
                </span>
              )}
            </p>
            <button
              onClick={() => navigate('/customer/jobs')}
              data-testid="payment-back-to-orders"
              className="mt-6 inline-flex items-center gap-2 bg-[#001d4c] hover:bg-[#0a2c63] text-white font-semibold rounded-lg px-5 py-3 text-sm transition-colors"
            >
              Zu meinen Aufträgen
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
        {(expired || timeout || error) && (
          <div className="text-center" data-testid="payment-status-error">
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-9 h-9 text-amber-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-[#001d4c]">
              {expired ? 'Zahlung abgelaufen' : timeout ? 'Bestätigung dauert länger als gewohnt' : 'Zahlung konnte nicht verifiziert werden'}
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              {expired
                ? 'Die Zahlungs-Session ist abgelaufen. Sie können die Bezahlung in Ihren Aufträgen erneut starten.'
                : timeout
                  ? 'Falls Sie bereits bezahlt haben, prüfen Sie bitte in Kürze nochmals den Status in Ihren Aufträgen.'
                  : 'Bitte prüfen Sie den Status in Ihren Aufträgen oder kontaktieren Sie uns.'}
            </p>
            <button
              onClick={() => navigate('/customer/jobs')}
              className="mt-6 inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium rounded-lg px-5 py-3 text-sm transition-colors"
            >
              Zu meinen Aufträgen
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default CustomerPaymentSuccess;
